import { defineStore } from 'pinia'

import { StoreId } from '@/store'
import {
  City,
  CityState,
  FooterContents,
  SocialNetwork,
} from '@/store/city/city.types'
import { getCityDomainForHost } from '@/store/city/city.utils'
import { OptionsPhone } from '@/store/login/login.types'

import { Success } from '@/api/results'

export const useCityStore = defineStore(StoreId.CITY, {
  state: (): CityState => {
    return {
      cityInfo: {
        is_alias: false,
        domain_prefix: '',
        verification_code_length: 0,
        wok_constructor_enabled: false,
        domain: '',
        current_time: '',
        timezone: '',
        is_working_now: false,
        slot_machine_enabled: false,
        padeg: {
          pred: '',
          dat: '',
          imen: '',
        },
        working_hours: null,
        language: '',
        country: null,
        state: null,
        phone: '',
        phone_secondary: '',
        social_networks: null,
        is_active: false,
        footer_contents: null,
        tel_input_mask: '',
        tel_input_placeholder: '',
        visible_name: '',
      },
      cityList: [],
    }
  },
  actions: {
    async getCityInfo(currentCity: string) {
      const { $apiService } = useNuxtApp()
      const result = await $apiService.city.fetchCityInfo(currentCity)

      if (result instanceof Success) this.cityInfo = result.data

      return result
    },

    async fetchCityList() {
      const { $apiService, $host } = useNuxtApp()
      const response = await $apiService.city.fetchCityList()

      if (!(response instanceof Success)) {
        return
      }

      // убираем из списка город в котором находимся
      const currentCity = this.cityInfo.domain_prefix
      let cityList = response.data.filter(
        (city) => city.domain_prefix !== currentCity,
      )

      // патчим список городов добавляя поле с ссылкой на сайт города
      cityList = cityList.map((city) => {
        return {
          ...city,
          cityUrl: `//${getCityDomainForHost($host, city.domain)}`,
        }
      })

      // сортируем список по алфавиту
      cityList.sort((a: City, b: City) =>
        a.visible_name.localeCompare(b.visible_name, 'ru'),
      )
      // сортируем список по странам
      cityList.sort((a: City, b: City) =>
        a.country.name.localeCompare(b.country.name, 'ru'),
      )

      this.cityList = cityList
    },

    findLinkByName(name: string) {
      const footerContents = this.cityInfo.footer_contents

      if (!footerContents) {
        return
      }

      for (const footerContent of footerContents) {
        for (const footerItem of footerContent.items) {
          if (footerItem.label === name) {
            return footerItem
          }
        }
      }
    },
  },
  getters: {
    phones: ({ cityInfo }): string[] => {
      const phones: string[] = []
      const phone: string = cityInfo.phone
      const secondaryPhone: string = cityInfo.phone_secondary

      if (phone) phones.push(phone)

      if (secondaryPhone) phones.push(secondaryPhone)

      return phones
    },
    optionsPhone: ({ cityInfo }): OptionsPhone => {
      return {
        mask: cityInfo?.tel_input_mask,
        placeholder: cityInfo?.tel_input_placeholder,
        code: cityInfo?.country?.code || 0,
        get length() {
          return this.mask.match(/#/g)?.length || 0
        },
      }
    },
    socialNetworks: ({ cityInfo }): SocialNetwork[] =>
      cityInfo?.social_networks || [],
    findCompanyLinks: ({ cityInfo }: CityState) => {
      if (cityInfo.footer_contents) {
        return cityInfo.footer_contents.find(
          (content: FooterContents) => content.name === 'Компания',
        )
      }
    },
    copyright: ({ cityInfo }): string => {
      const year = new Date(cityInfo.current_time).getFullYear()

      return `2010-${year}, FARFOR`
    },
    currencySymbol: ({ cityInfo }) => cityInfo?.country?.currency.symbol,
    currencyCode: ({ cityInfo }) => cityInfo?.country?.currency.code,
    cityName: ({ cityInfo }) => cityInfo.padeg?.imen,
    cityState: ({ cityInfo }) => cityInfo.state,
    isAlias: ({ cityInfo }) => cityInfo.is_alias,
    cityServerTimeISO: ({ cityInfo }) => cityInfo.current_time,
    timezone: ({ cityInfo }) => cityInfo.timezone,
  },
})
